import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { dataApi } from '@/api/data';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { LineChart, StatCard } from '@/components/charts';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { format, subDays, subMonths } from 'date-fns';
import type { DailyDataResponse, MonthlySummary } from '@/types/data';

export default function Dashboard() {
  const { user, accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // 今月と先月のサマリー
  const [thisMonthSummary, setThisMonthSummary] = useState<MonthlySummary | null>(null);
  const [lastMonthSummary, setLastMonthSummary] = useState<MonthlySummary | null>(null);

  // 直近30日のデータ（グラフ用）
  const [dailyData, setDailyData] = useState<DailyDataResponse | null>(null);

  // 初期法人選択
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // データ取得
  useEffect(() => {
    if (!selectedCompanyId) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const today = new Date();
        const thisMonth = today;
        const lastMonth = subMonths(today, 1);

        // 直近30日間のデータを取得
        const startDate = format(subDays(today, 30), 'yyyy-MM-dd');
        const endDate = format(today, 'yyyy-MM-dd');

        const [daily, thisMonthData, lastMonthData] = await Promise.all([
          dataApi.getDailyData({
            company_id: selectedCompanyId,
            start_date: startDate,
            end_date: endDate,
            include_cumulative: true,
          }),
          dataApi.getMonthlySummary({
            company_id: selectedCompanyId,
            year: thisMonth.getFullYear(),
            month: thisMonth.getMonth() + 1,
          }).catch(() => null),
          dataApi.getMonthlySummary({
            company_id: selectedCompanyId,
            year: lastMonth.getFullYear(),
            month: lastMonth.getMonth() + 1,
          }).catch(() => null),
        ]);

        setDailyData(daily);
        setThisMonthSummary(thisMonthData);
        setLastMonthSummary(lastMonthData);
      } catch (error) {
        console.error('データ取得エラー:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedCompanyId]);

  // 昨日と一昨日のデータを計算
  const dailyStats = useMemo(() => {
    if (!dailyData?.data || dailyData.data.length === 0) {
      return { yesterday: null, dayBefore: null };
    }

    // 全ラベルの日次値を日付ごとに合計
    const dailyTotals: Record<string, number> = {};
    dailyData.data.forEach(label => {
      label.values.forEach(v => {
        if (!dailyTotals[v.date]) {
          dailyTotals[v.date] = 0;
        }
        dailyTotals[v.date] += v.daily_value;
      });
    });

    const sortedDates = Object.keys(dailyTotals).sort().reverse();
    const yesterday = sortedDates[0] ? { date: sortedDates[0], value: dailyTotals[sortedDates[0]] } : null;
    const dayBefore = sortedDates[1] ? { date: sortedDates[1], value: dailyTotals[sortedDates[1]] } : null;

    return { yesterday, dayBefore };
  }, [dailyData]);

  // 昨日の前日比を計算
  const yesterdayChange = useMemo(() => {
    if (!dailyStats.yesterday || !dailyStats.dayBefore || dailyStats.dayBefore.value === 0) {
      return null;
    }
    return ((dailyStats.yesterday.value - dailyStats.dayBefore.value) / dailyStats.dayBefore.value) * 100;
  }, [dailyStats]);

  // 今月の前月比を計算
  const monthlyChanges = useMemo(() => {
    const salesChange = thisMonthSummary?.total_sales && lastMonthSummary?.total_sales
      ? ((Number(thisMonthSummary.total_sales) - Number(lastMonthSummary.total_sales)) / Number(lastMonthSummary.total_sales)) * 100
      : null;

    const customerChange = thisMonthSummary?.customer_count && lastMonthSummary?.customer_count
      ? ((Number(thisMonthSummary.customer_count) - Number(lastMonthSummary.customer_count)) / Number(lastMonthSummary.customer_count)) * 100
      : null;

    return { salesChange, customerChange };
  }, [thisMonthSummary, lastMonthSummary]);

  // 日次データをチャート用に変換（新規登録者数）
  const registrationChartData = useMemo(() => {
    if (!dailyData?.data || dailyData.data.length === 0) return [];

    // 全ラベルの日次値を日付ごとに合計
    const dailyTotals: Record<string, number> = {};
    dailyData.data.forEach(label => {
      label.values.forEach(v => {
        if (!dailyTotals[v.date]) {
          dailyTotals[v.date] = 0;
        }
        dailyTotals[v.date] += v.daily_value;
      });
    });

    return Object.entries(dailyTotals)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({
        date: format(new Date(date), 'M/d'),
        value,
      }));
  }, [dailyData]);

  // 累計データをチャート用に変換
  const cumulativeChartData = useMemo(() => {
    if (!dailyData?.data || dailyData.data.length === 0) return [];

    // 全ラベルの累計値を日付ごとに合計
    const dailyTotals: Record<string, number> = {};
    dailyData.data.forEach(label => {
      label.values.forEach(v => {
        if (!dailyTotals[v.date]) {
          dailyTotals[v.date] = 0;
        }
        dailyTotals[v.date] += v.cumulative_value || 0;
      });
    });

    return Object.entries(dailyTotals)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({
        date: format(new Date(date), 'M/d'),
        value,
      }));
  }, [dailyData]);

  const companyOptions = accessibleCompanies.map((c) => ({
    value: c.id,
    label: c.display_name,
  }));

  const today = new Date();

  return (
    <div className="space-y-4 sm:space-y-6 min-w-0">
      {/* ヘッダー */}
      <div className="space-y-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">ダッシュボード</h1>
          <p className="text-sm text-gray-500">
            ようこそ、{user?.full_name}さん
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-4">
          {companyOptions.length > 1 ? (
            <Select
              options={companyOptions}
              value={selectedCompanyId?.toString() || ''}
              onChange={(value) => setSelectedCompanyId(Number(value))}
              className="w-full sm:w-48"
            />
          ) : (
            <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-900">
              {companyOptions[0]?.label || '-'}
            </div>
          )}
        </div>
      </div>

      {accessibleCompanies.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-700">
            アクセス可能な法人がありません。管理者にお問い合わせください。
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {/* 今月の統計 */}
          <div>
            <h2 className="text-sm font-medium text-gray-500 mb-2">
              今月（{today.getFullYear()}年{today.getMonth() + 1}月）
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatCard
                title="売上"
                value={`¥${Math.round(Number(thisMonthSummary?.total_sales ?? 0)).toLocaleString()}`}
                change={monthlyChanges.salesChange !== null ? {
                  value: monthlyChanges.salesChange,
                  label: '前月比',
                } : undefined}
              />
              <StatCard
                title="顧客数"
                value={Math.round(Number(thisMonthSummary?.customer_count ?? 0)).toLocaleString()}
                change={monthlyChanges.customerChange !== null ? {
                  value: monthlyChanges.customerChange,
                  label: '前月比',
                } : undefined}
              />
              <StatCard
                title="平均客単価"
                value={`¥${Math.round(Number(thisMonthSummary?.average_order_value ?? 0)).toLocaleString()}`}
              />
              <StatCard
                title="成約率"
                value={`${(Number(thisMonthSummary?.conversion_rate ?? 0) * 100).toFixed(1)}%`}
              />
            </div>
          </div>

          {/* 昨日の統計 */}
          <div>
            <h2 className="text-sm font-medium text-gray-500 mb-2">
              昨日（{dailyStats.yesterday ? format(new Date(dailyStats.yesterday.date), 'M月d日') : '-'}）
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatCard
                title="新規登録数"
                value={dailyStats.yesterday?.value.toLocaleString() ?? '-'}
                change={yesterdayChange !== null ? {
                  value: yesterdayChange,
                  label: '前日比',
                } : undefined}
              />
              <StatCard
                title="前日の登録数"
                value={dailyStats.dayBefore?.value.toLocaleString() ?? '-'}
              />
            </div>
          </div>

          {/* チャートエリア */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* 新規登録者数の推移 */}
            <Card>
              <CardHeader>
                <CardTitle>新規登録者数の推移（日別）</CardTitle>
              </CardHeader>
              <CardContent>
                {registrationChartData.length > 0 ? (
                  <LineChart
                    data={registrationChartData}
                    lines={[
                      { dataKey: 'value', name: '新規登録数', color: '#3B82F6' },
                    ]}
                    height={300}
                    yAxisFormatter={(value) => value.toLocaleString()}
                  />
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    データがありません
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 累計登録者数の推移 */}
            <Card>
              <CardHeader>
                <CardTitle>累計登録者数の推移</CardTitle>
              </CardHeader>
              <CardContent>
                {cumulativeChartData.length > 0 ? (
                  <LineChart
                    data={cumulativeChartData}
                    lines={[
                      { dataKey: 'value', name: '累計登録数', color: '#10B981' },
                    ]}
                    height={300}
                    yAxisFormatter={(value) => value.toLocaleString()}
                  />
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    データがありません
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* クイックスタート */}
          <Card>
            <CardHeader>
              <CardTitle>クイックスタート</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <a
                  href="/analysis"
                  className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <h3 className="font-medium text-blue-900">データ分析</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    売上・クリック率・データ一覧を分析
                  </p>
                </a>
                <a
                  href="/data-table"
                  className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <h3 className="font-medium text-green-900">データ一覧</h3>
                  <p className="text-sm text-green-700 mt-1">
                    全ラベルのデータを確認
                  </p>
                </a>
                <a
                  href="/admin/scraping"
                  className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <h3 className="font-medium text-purple-900">スクレイピング</h3>
                  <p className="text-sm text-purple-700 mt-1">
                    データの取得と更新
                  </p>
                </a>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
